import { useCallback, useEffect, useState } from 'react';
import { useRouter } from '@tanstack/react-router';

/**
 * The pages a person has open, as tabs along the top bar (owner, 6 October 2026: "if you open say
 * extensions its a tab on the top bar, then if you open dialing restrictions it's a new tab then
 * we can switch between them easily, also close them with a x").
 *
 * A tab is a section, and it remembers where in the section it was: the seat, and which of its
 * settings, under Extensions. Going back to a tab goes back there, scrolled where it was left.
 * The address is still the truth -- a tab is opened by being navigated to, from the menu, a link
 * or a typed address -- and the list is kept for this browser tab only (sessionStorage), so a
 * second window starts with the page it opened.
 *
 * What a page holds that is not in its address -- something typed and not yet saved -- is not
 * kept: switching away unmounts the page, as following any link does. Most settings save as they
 * change.
 */
export interface PageTab {
  /** The section: one tab each. */
  key: string;
  /** Where in it the tab was last. */
  path: string;
  /** When it was last shown, for choosing which to close when there are too many. */
  used: number;
}

/** More than this and the least recently shown one closes: past it, labels shrink to nothing. */
const MAX_TABS = 10;

function load(storageKey: string): PageTab[] {
  try {
    const v: unknown = JSON.parse(sessionStorage.getItem(storageKey) ?? '[]');
    if (!Array.isArray(v)) return [];
    return v.filter((t): t is PageTab => !!t && typeof t === 'object'
      && typeof (t as PageTab).key === 'string' && typeof (t as PageTab).path === 'string'
      && (t as PageTab).path.startsWith('/'))
      .map((t) => ({ key: t.key, path: t.path, used: typeof t.used === 'number' ? t.used : 0 }));
  } catch {
    return [];
  }
}

function save(storageKey: string, tabs: PageTab[]): void {
  try { sessionStorage.setItem(storageKey, JSON.stringify(tabs)); } catch { /* private window: tabs last the visit */ }
}

/** How far down each tab was, for this visit. */
const scrolled = new Map<string, number>();

export interface PageTabs {
  tabs: PageTab[];
  /** The tab showing, when the page showing is one. */
  current: string | null;
  /** Back to a tab, where it was left. */
  show: (key: string) => void;
  /** Closes a tab; closing the one showing shows its neighbour. */
  close: (key: string) => void;
}

export function usePageTabs({ storageKey, current, path, home }: {
  /** Whose tabs these are: the console's, or one account's. */
  storageKey: string;
  /** The section showing, or null for a page that is not one. */
  current: string | null;
  /** The address showing. */
  path: string;
  /** Where to go when the tab showing closes and there is no other. */
  home: string;
}): PageTabs {
  const router = useRouter();
  const [store, setStore] = useState(() => ({ key: storageKey, tabs: load(storageKey) }));
  // Another account's console: its own tabs.
  const active = store.key === storageKey ? store : { key: storageKey, tabs: load(storageKey) };
  if (active !== store) setStore(active);

  // Whatever is showing is a tab, at the address it is showing.
  useEffect(() => {
    if (!current) return;
    setStore((s) => {
      const now = Date.now();
      let tabs = s.tabs.some((t) => t.key === current)
        ? s.tabs.map((t) => (t.key === current ? { ...t, path, used: now } : t))
        : [...s.tabs, { key: current, path, used: now }];
      while (tabs.length > MAX_TABS) {
        const oldest = tabs.filter((t) => t.key !== current).sort((a, b) => a.used - b.used)[0];
        tabs = tabs.filter((t) => t !== oldest);
      }
      save(s.key, tabs);
      return { ...s, tabs };
    });
  }, [current, path, storageKey]);

  const go = useCallback((to: string, key: string) => {
    router.history.push(to);
    // After the page has drawn from what is cached, and again once a slower one has.
    const y = scrolled.get(`${storageKey}|${key}`) ?? 0;
    requestAnimationFrame(() => window.scrollTo(0, y));
    window.setTimeout(() => window.scrollTo(0, y), 150);
  }, [router, storageKey]);

  const show = useCallback((key: string) => {
    if (key === current) return;
    const tab = active.tabs.find((t) => t.key === key);
    if (!tab) return;
    if (current) scrolled.set(`${storageKey}|${current}`, window.scrollY);
    go(tab.path, key);
  }, [active.tabs, current, go, storageKey]);

  const close = useCallback((key: string) => {
    const i = active.tabs.findIndex((t) => t.key === key);
    if (i < 0) return;
    const rest = active.tabs.filter((t) => t.key !== key);
    save(storageKey, rest);
    setStore({ key: storageKey, tabs: rest });
    scrolled.delete(`${storageKey}|${key}`);
    if (key !== current) return;
    const next = rest[i] ?? rest[i - 1];
    if (next) go(next.path, next.key); else router.history.push(home);
  }, [active.tabs, current, go, home, router, storageKey]);

  return { tabs: active.tabs, current, show, close };
}
